import { useEffect, useMemo, useState } from 'react';
import { Grid, Box, Typography } from '@mui/material';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Rectangle,
  Label,
  ReferenceLine,
  Line,
  ComposedChart,
} from 'recharts';

import { ThemePalette } from 'mui.theme';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import { FlexBox } from 'components/Containers/FlexBox';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { truncateTo2dp } from 'utils/helpers/general';
import {
  isSumZeroOrEmpty,
  filterDataByPeriod,
  getUtilizationOptionLabel,
} from 'views/Utilization/utils';
import { periodMap } from 'views/Production/AllFleet/utils';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const _palette = clientThemePalette[CLIENT_THEME];

interface Props {
  data: any[];
  setRollingDailyAverage: (value: number) => void;
  setUserSelections: any;
  selectedChartSource: ChartSource;
  setSelectedChartSource: any;
}

type ChartSource =
  | 'Last 7 Days'
  | 'Last 14 Days'
  | 'Last 4 Weeks'
  | 'Last 6 Months';

// const periodMap: Record<ChartSource, string> = {
//   'Last 7 Days': '7days',
//   'Last 14 Days': '14days',
//   'Last 4 Weeks': '1month',
//   'Last 6 Months': '6months',
// };

const options = [
  'Last 7 Days',
  'Last 14 Days',
  'Last 4 Weeks',
  'Last 6 Months',
];

interface ChartEntry {
  date: string;
  value: number;
  period: string;
}

const mergeChartDataAndMovingAverages = (
  chartData: any[],
  movingAverages: Array<{
    date: string;
    movingAverage: number | null;
    period: string;
  }>,
  selectedChartSource: ChartSource
): Array<{
  date: string;
  value: number;
  movingAverage: number | null;
  period: string;
}> => {
  // logic to set the initial values for moving averages based on the selected window (selectedChartSource)

  if (
    selectedChartSource === 'Last 7 Days' ||
    selectedChartSource === 'Last 14 Days'
  ) {
    if (movingAverages?.length > 0) {
      movingAverages[0].movingAverage =
        (chartData[0]?.total_engine_working_minutes || 0) / 60;
    }
  } else if (selectedChartSource === 'Last 4 Weeks') {
    if (movingAverages?.length >= 2) {
      movingAverages[0].movingAverage =
        (chartData[0]?.total_engine_working_minutes || 0) / 60;
      movingAverages[1].movingAverage =
        chartData[1].total_engine_working_minutes / 60;
    }
  } else if (selectedChartSource === 'Last 6 Months') {
    if (movingAverages?.length >= 5) {
      for (let i = 0; i < 6; i++) {
        movingAverages[i].movingAverage =
          chartData[i].total_engine_working_minutes / 60;
      }
    }
  }

  return chartData.map((entry) => {
    const movingAverageEntry = movingAverages.find(
      (ma) => ma.date === entry.date
    );

    return {
      ...entry,
      movingAverage: movingAverageEntry
        ? Number(Number(movingAverageEntry?.movingAverage).toFixed(2))
        : null,
    };
  });
};

const calculateDailyMovingAverage = (
  data: ChartEntry[],
  windowSize: number
): Array<{ date: string; movingAverage: number | null; period: string }> => {
  // Sort the data by date in ascending order
  const sortedData = [...data].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const movingAverages: Array<{
    date: string;
    movingAverage: number | null;
    period: string;
  }> = [];

  // Iterate over the sorted data and ensure all dates are covered
  for (let i = 0; i < sortedData.length; i++) {
    const windowData = sortedData.slice(Math.max(0, i - windowSize + 1), i + 1);

    const windowSum = windowData.reduce(
      (sum, entry) => sum + Number(entry.value),
      0
    );

    const windowAverage =
      windowData.length === windowSize ? windowSum / windowSize : null;

    movingAverages.push({
      date: sortedData[i].date,
      period: sortedData[i].period,
      movingAverage: windowAverage,
    });
  }

  return movingAverages;
};

const Chart = ({
  data,
  setRollingDailyAverage,
  setUserSelections,
  selectedChartSource,
  setSelectedChartSource,
}: Props) => {
  const { t } = useTranslation();
  const isLoading = useAppSelector(
    (state) =>
      state.utilizationReducer.loadingStates.isLoadingFleetPeriodSummary
  );

  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme ? _palette.dark : _palette.light;
  const [chartData, setChartData] = useState<any[]>([]);

  const formatTimestamp = (timestamp: string): string => {
    const formattedDate = convertStdDateString(timestamp);
    return formattedDate;
  };

  const getHighestValue = (arr: any) => {
    const highestValue: number = arr.reduce((max: any, obj: any) => {
      const currentValue = Number(obj.value);
      return !isNaN(currentValue) && currentValue > max ? currentValue : max;
    }, -Infinity);

    return highestValue;
  };

  useEffect(() => {
    if (data) {
      const filteredData = filterDataByPeriod(
        data,
        periodMap[selectedChartSource]
      ).map((item) => ({
        ...item,
        value: truncateTo2dp(Number(item.total_engine_working_minutes) / 60),
      }));

      setChartData(filteredData);
    }
  }, [data, selectedChartSource]);

  const average = useMemo(
    () =>
      chartData.reduce(
        (sum: number, entry: { value: string }) => sum + Number(entry.value),
        0
      ) / chartData.length,
    [chartData]
  );

  // Calculate the moving averages - If selectedChartSource is last 7 or last 14 days then keep window size as 2, if selectedChartSource is last 4 weeks then keep window size as 3, else keep window size as 7
  const movingAverages = useMemo(
    () =>
      calculateDailyMovingAverage(
        chartData,
        selectedChartSource === 'Last 7 Days' ||
          selectedChartSource === 'Last 14 Days'
          ? 2
          : selectedChartSource === 'Last 4 Weeks'
          ? 3
          : 7
      ),
    [chartData, selectedChartSource]
  );
  const mergedChartData = useMemo(
    () =>
      mergeChartDataAndMovingAverages(
        chartData,
        movingAverages,
        selectedChartSource
      ),
    [chartData, movingAverages, selectedChartSource]
  );

  useEffect(() => {
    setUserSelections({
      showChart: selectedChartSource,
      movingAverages,
      average,
    });
  }, [selectedChartSource, movingAverages, average]);

  useEffect(() => {
    const result = calculateDailyMovingAverage(chartData, 2);
    if (result?.length > 0 && Array.isArray(result)) {
      setRollingDailyAverage(result[result.length - 1].movingAverage ?? 0);
    }
  }, [chartData]);

  return (
    <Grid item lg={6} md={6} sm={12}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.borderGrey}`,
          minHeight: '300px',
          maxHeight: '300px',
          position: 'relative',
        }}
      >
        <Typography
          sx={{
            fontWeight: '600',
            fontSize: '13px',
          }}
        >
          {t('daily_productive_hours')}
        </Typography>

        <Box
          sx={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: palette.textSecondary,
            }}
          >
            {t('utilization_show_label')}
          </Typography>

          <ProductionSelector
            options={options}
            selected={selectedChartSource}
            setSelected={setSelectedChartSource}
            minWidth="150px"
            getOptionLabel={(option) => getUtilizationOptionLabel(t, option)}
          />
        </Box>

        {isLoading ? (
          <MultiColorCircularLoader height="300px" />
        ) : (
          <Box
            sx={{
              paddingTop: '15px',
            }}
          >
            {isSumZeroOrEmpty(chartData, 'value') ? (
              <Typography
                sx={{
                  fontSize: '11px',
                }}
              >
                {t('no_chart_data_available_4')}
              </Typography>
            ) : (
              <FlexBox
                sx={{
                  maxWidth: 'inherit',
                  height: '230px',
                  paddingTop: '20px',
                  marginRight: '10px',
                  paddingBottom: '10px',
                }}
              >
                {mergedChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      width={700}
                      height={300}
                      data={mergedChartData}
                      margin={{
                        top: 20,
                        right: 110,
                        left: 5,
                        bottom: 5,
                      }}
                      barSize={30}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={'#808080'}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="date"
                        tickFormatter={formatTimestamp}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis
                        tick={{ fontSize: 10 }}
                        label={{
                          value: t('productive_hours'),
                          angle: 270,
                          position: 'insideLeft',
                          fontSize: 10,
                          textAnchor: 'middle',
                          dy: 50,
                        }}
                        domain={[0, getHighestValue(data)]}
                      />
                      <Label
                        style={{
                          textAnchor: 'middle',
                          fontSize: '11px',
                        }}
                        angle={270}
                        value={t('productive_hours')}
                      />
                      <Tooltip
                        cursor={{
                          fill: enableDarkTheme
                            ? 'rgba(0,0,0,0.15)'
                            : '#CCCCCC',
                        }}
                        wrapperStyle={{ outline: 'none', fontSize: '11px' }}
                        contentStyle={{
                          backgroundColor: enableDarkTheme
                            ? palette.primary
                            : ThemePalette.typography.wheat,
                        }}
                        itemStyle={{
                          color: enableDarkTheme
                            ? ThemePalette.typography.wheat
                            : ThemePalette.typography.black,
                        }}
                        labelFormatter={(value: string) =>
                          convertStdDateString(value)
                        }
                      />
                      <Bar
                        dataKey="value"
                        fill={_palette.dark.primary}
                        activeBar={<Rectangle fill="pink" stroke="blue" />}
                      />
                      <Line
                        type="monotone"
                        dataKey="movingAverage"
                        stroke={
                          enableDarkTheme
                            ? '#fafafa'
                            : ThemePalette.typography.black
                        }
                        dot={{ r: 0 }}
                        strokeDasharray={'3 3'}
                      />

                      <ReferenceLine
                        y={average}
                        label={{
                          value: t('utilization_avg_hours_per_day', {
                            average: average.toFixed(2),
                          }),
                          position: 'right',
                          fontSize: '10px',
                        }}
                        stroke="#FF6600"
                        strokeDasharray="3 3"
                        isFront
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: palette.textSecondary,
                    }}
                  >
                    {t('no_data_available_currently')}
                  </Typography>
                )}
              </FlexBox>
            )}
          </Box>
        )}
      </Box>
    </Grid>
  );
};

export default Chart;
